import { PhysioLayout } from "@/components/layout/physio-layout";
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

const MAITLAND_GRADES = [
  { grade: "Grade I", description: "Small amplitude movements at the beginning of range", useCase: "Severe pain at rest or with movement; used to modulate pain without provoking symptoms", neurophysiological: "Gate control pain inhibition; central sensitisation modulation" },
  { grade: "Grade II", description: "Large amplitude movements within the free range, not reaching the limit", useCase: "Moderate pain with movement; good for maintaining range without provoking end-range symptoms", neurophysiological: "Mechanoreceptor stimulation; hypoalgesia via DNIC" },
  { grade: "Grade III", description: "Large amplitude movements into resistance, reaching the limit of range", useCase: "Stiffness and reduced range as the primary symptom; pain not the dominant issue", neurophysiological: "Tissue extensibility; collagen remodelling; mechanoreceptor activation" },
  { grade: "Grade IV", description: "Small amplitude movements at the limit of range", useCase: "End-range stiffness; chronic restriction; mobilisation of resistant hypomobility", neurophysiological: "Collagen fibre alignment; end-range mechanoreceptor activation" },
  { grade: "Grade V", description: "Manipulation — high velocity, low amplitude thrust at the limit of range", useCase: "Specific joint restriction not responding to Grades I–IV; where manipulation is safe and indicated", neurophysiological: "Cavitation; central pain inhibition; possible neurological reflex effects" },
];

const CONDITIONS = [
  {
    condition: "Rheumatoid Arthritis (RA)",
    colour: "border-red-200 bg-red-50",
    badge: "bg-red-100 text-red-700",
    alert: true,
    precautions: [
      { text: "ATLANTOAXIAL INSTABILITY (AAI) — Critical precaution. Screen ALL RA patients for cervical instability before any cervical manual therapy. AAI occurs in up to 30–40% of RA patients (radiological). Can be asymptomatic. Always ask about: occipital headache, Lhermitte's sign, hand/arm tingling, sudden onset neck pain, gait disturbance.", severity: "critical" },
      { text: "Cervical manipulation (Grade V) — ABSOLUTELY CONTRAINDICATED in RA. Subluxation risk. Do not perform high-velocity cervical thrust techniques.", severity: "contraindicated" },
      { text: "Active joint inflammation — avoid direct mobilisation of acutely inflamed joints. Treat adjacent joints and surrounding soft tissue instead.", severity: "caution" },
      { text: "Severe joint damage/destruction — radiological screening before mobilisation of significantly eroded joints (especially wrist, MTP, MCP joints)", severity: "caution" },
      { text: "Osteoporosis (common in RA due to disease and steroids) — avoid high-force techniques; Grade III/IV with extreme caution; no spinal manipulation", severity: "caution" },
    ],
    safe: [
      "Gentle Grade I–II cervical techniques for pain modulation ONLY if AAI excluded",
      "Peripheral joint mobilisation (shoulder, elbow, wrist, hip, knee) — Grade I–III in stable disease",
      "Temporomandibular joint mobilisation for jaw involvement",
      "Soft tissue massage to paraspinal and peri-articular muscles",
      "Neural mobilisation techniques for peripheral neuropathy symptoms",
    ],
    screening: "Undertake cervical spine screening questionnaire before any cervical assessment. Consider X-ray odontoid view or CT if symptomatic or high disease burden. Always assess neurology (upper limb, lower limb) before and after treatment.",
  },
  {
    condition: "Axial Spondyloarthritis (axSpA)",
    colour: "border-amber-200 bg-amber-50",
    badge: "bg-amber-100 text-amber-700",
    alert: false,
    precautions: [
      { text: "Advanced ankylosis — vertebral fracture risk with manipulation. Fused segments are at high risk of fracture from minor trauma. No Grade V thrust techniques where ankylosis is present or suspected.", severity: "contraindicated" },
      { text: "Cervical ankylosis — as above; distinguish from AAI (less common in axSpA than RA). Carefully review X-ray/MRI before any cervical technique.", severity: "caution" },
      { text: "Acute uveitis — systemic inflammation; avoid aggressive mobilisation during active flare", severity: "caution" },
      { text: "Bisphosphonate use — indicates osteoporosis risk; modify force accordingly", severity: "caution" },
    ],
    safe: [
      "Grade I–II spinal mobilisation for pain in non-fused segments",
      "Grade III–IV thoracic mobilisation for improving mobility (not where fused)",
      "Peripheral joint mobilisation (hip, shoulder — commonly affected in axSpA)",
      "Soft tissue techniques: paraspinal muscle release, myofascial techniques",
      "Rib articulation techniques for thoracic mobility and chest expansion",
    ],
    screening: "Review imaging before intervention. Look for bamboo spine or advanced ankylosis on X-ray. If complete cervical or thoracic fusion, manual therapy is limited to soft tissue and peripheral joints.",
  },
  {
    condition: "Systemic Lupus Erythematosus (SLE)",
    colour: "border-purple-200 bg-purple-50",
    badge: "bg-purple-100 text-purple-700",
    alert: false,
    precautions: [
      { text: "Avascular necrosis (AVN) — occurs in 5–15% of SLE patients (hip, knee, shoulder). Rule out AVN before weight-bearing joint mobilisation. Presents as hip/knee pain in a steroid-treated patient.", severity: "caution" },
      { text: "Anticoagulation (many SLE patients on warfarin/DOAC for APS) — avoid deep tissue techniques that could cause haematoma; confirm INR before treatment", severity: "caution" },
      { text: "Active vasculitis — avoid manipulation over skin fragility areas", severity: "caution" },
      { text: "Osteoporosis (corticosteroid-induced) — common in SLE; reduce force of techniques", severity: "caution" },
    ],
    safe: [
      "Gentle peripheral joint mobilisation for arthralgia",
      "Soft tissue techniques for myalgia and muscle tension",
      "Neural mobilisation for peripheral neuropathy",
      "Grade I–II spinal techniques for pain modulation during flare",
    ],
    screening: "Check anticoagulation status and recent blood results. Screen for AVN in steroid-treated patients before hip or knee mobilisation.",
  },
  {
    condition: "Systemic Sclerosis (SSc)",
    colour: "border-slate-200 bg-slate-50",
    badge: "bg-slate-100 text-slate-600",
    alert: false,
    precautions: [
      { text: "Skin fragility — avoid pressure on calcinosis deposits; protect digital ulcer sites from any manual contact", severity: "caution" },
      { text: "Pulmonary hypertension (PAH) — exertion risk; no high-force techniques that might cause breath-holding or Valsalva manoeuvre", severity: "caution" },
      { text: "Gastro-oesophageal involvement — avoid prone positioning if symptomatic reflux", severity: "caution" },
    ],
    safe: [
      "Gentle hand and finger joint mobilisation (Grade I–II) for digital contractures — in warm water where possible",
      "Facial mobilisation for microstomia",
      "Temporomandibular joint mobilisation",
      "Gentle thoracic mobilisation for chest expansion",
      "Soft tissue techniques: MFR (myofascial release) to fibrotic areas",
    ],
    screening: "Skin examination before manual therapy. Identify calcinosis, ulcers, and areas of extreme skin fragility. Document and avoid.",
  },
];

const SOFT_TISSUE = [
  { name: "Myofascial Release (MFR)", description: "Low-load, prolonged stretch to myofascial tissue. Well-tolerated in inflammatory arthritis. Reduce paraspinal muscle tension secondary to guarding.", suitable: "All conditions — particularly RA, axSpA, SLE" },
  { name: "Trigger Point Therapy", description: "Sustained pressure to myofascial trigger points. Use cautiously in anticoagulated patients. Avoid over inflamed joint areas.", suitable: "RA (soft tissue pain), axSpA (paraspinal muscles), SLE (myalgia)" },
  { name: "Massage (Effleurage, Petrissage)", description: "Superficial and deep massage for muscle relaxation, circulation improvement, and pain modulation. Avoid over acutely inflamed joints.", suitable: "All conditions — moderate pressure. Avoid digital ulcer sites in SSc." },
  { name: "Lymphatic Drainage", description: "Gentle technique for oedema secondary to joint inflammation or medication side effects. Particularly useful in RA with periarticular swelling.", suitable: "RA, SLE with oedema" },
  { name: "Proprioceptive Neuromuscular Facilitation (PNF) Stretching", description: "Contract-relax technique to gain ROM. Avoid in acute inflammation. Useful for hip and shoulder mobility in axSpA.", suitable: "axSpA (hip, thorax), RA in stable disease" },
];

export default function ManualTherapy() {
  return (
    <PhysioLayout title="Manual Therapy Reference">
      <div className="space-y-8">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <h2 className="font-bold text-slate-900 mb-1">Important Safety Notice</h2>
            <p className="text-sm text-slate-700">Manual therapy in rheumatology requires careful clinical reasoning and condition-specific precaution screening. This reference provides clinical guidance but does not replace individual patient assessment. Cervical manipulation (Grade V) is contraindicated in RA due to atlantoaxial instability risk.</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-bold text-slate-900 mb-4">Maitland Grading System</h3>
          <p className="text-sm text-slate-600 mb-5">The Maitland concept uses oscillatory joint mobilisation graded I–IV (plus Grade V manipulation) to address pain and stiffness. Selection of grade depends on whether pain or stiffness dominates, and the position within the joint's resistance diagram.</p>
          <div className="space-y-3">
            {MAITLAND_GRADES.map((g) => (
              <div key={g.grade} className="grid grid-cols-1 md:grid-cols-4 gap-3 border border-slate-100 rounded-xl p-4">
                <div>
                  <span className={`inline-block text-sm font-bold px-3 py-1 rounded-full ${g.grade === "Grade V" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{g.grade}</span>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Description</p>
                  <p className="text-sm text-slate-700">{g.description}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Use When</p>
                  <p className="text-sm text-slate-700">{g.useCase}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Neurophysiology</p>
                  <p className="text-sm text-slate-600">{g.neurophysiological}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-sm text-red-800"><strong>Grade V in Rheumatology:</strong> High-velocity thrust manipulation is generally avoided or used with extreme caution in inflammatory arthritis. It is ABSOLUTELY CONTRAINDICATED in RA (cervical spine) and where ankylosis or significant structural damage is present (axSpA). Always reassess after each treatment. Document clinical reasoning thoroughly.</p>
          </div>
        </div>

        <div>
          <h3 className="font-bold text-slate-900 mb-4">Condition-Specific Precautions & Safe Techniques</h3>
          <div className="space-y-4">
            {CONDITIONS.map((cond) => (
              <div key={cond.condition} className={`rounded-2xl border p-6 ${cond.colour}`}>
                <div className="flex items-center gap-3 mb-4">
                  {cond.alert && <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />}
                  <h4 className="font-bold text-slate-900">{cond.condition}</h4>
                  {cond.alert && <span className="text-xs font-semibold bg-red-600 text-white px-2 py-0.5 rounded-full">HIGH ALERT</span>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <h5 className="font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
                      <XCircle className="h-4 w-4 text-red-500" /> Precautions & Contraindications
                    </h5>
                    <div className="space-y-2">
                      {cond.precautions.map((p, i) => (
                        <div key={i} className={`rounded-lg p-3 text-sm ${
                          p.severity === "critical" ? "bg-red-100 border border-red-300 text-red-900" :
                          p.severity === "contraindicated" ? "bg-red-50 border border-red-200 text-red-800" :
                          "bg-amber-50 border border-amber-200 text-amber-900"
                        }`}>
                          {p.severity === "critical" && <span className="font-bold text-red-700 mr-1">⚠ CRITICAL:</span>}
                          {p.severity === "contraindicated" && <span className="font-bold text-red-600 mr-1">✗ CONTRAINDICATED:</span>}
                          {p.text}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h5 className="font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Safe Techniques
                    </h5>
                    <ul className="space-y-1.5">
                      {cond.safe.map((s, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
                          {s}
                        </li>
                      ))}
                    </ul>
                    {cond.screening && (
                      <div className="mt-3 bg-white/70 rounded-xl p-3 border border-slate-200">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Screening</p>
                        <p className="text-xs text-slate-700">{cond.screening}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-bold text-slate-900 mb-4">Soft Tissue Techniques</h3>
          <div className="space-y-3">
            {SOFT_TISSUE.map((st) => (
              <div key={st.name} className="border border-slate-100 rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <h5 className="font-semibold text-slate-800 text-sm">{st.name}</h5>
                </div>
                <div>
                  <p className="text-sm text-slate-600">{st.description}</p>
                </div>
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Suitable for: </span>
                  <span className="text-xs text-slate-700">{st.suitable}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
          <h4 className="font-bold text-slate-900 mb-2">Documentation Reminder</h4>
          <p className="text-sm text-slate-600">All manual therapy for rheumatology patients must include: pre-treatment clinical reasoning documentation; contraindication screening with specific reference to atlantoaxial instability (RA) or ankylosis (axSpA); consent including risks; post-treatment neurological check where cervical or spinal mobilisation performed; documented plan for re-assessment. See CSP Standards of Practice 2021.</p>
        </div>
      </div>
    </PhysioLayout>
  );
}
